"use client";

import { usePathname } from "next/navigation";

import {
  isLocalizedPath,
  localePath,
  localeSpecs,
  unlocalizedPath,
  type Locale,
} from "@/i18n/config";
import { dictionaryFor } from "@/i18n/dictionaries";
import { saveLocalePreference } from "@/i18n/browser";

type LanguageSwitcherProps = {
  locale: Locale;
  onNavigate?: () => void;
  tabIndex?: number;
  variant?: "drawer" | "footer";
};

export function LanguageSwitcher({
  locale,
  onNavigate,
  tabIndex,
  variant = "footer",
}: LanguageSwitcherProps) {
  const pathname = usePathname();
  const basePath = unlocalizedPath(pathname);
  const available = isLocalizedPath(basePath);
  const label = dictionaryFor(locale).global.languageSwitcherLabel;

  return (
    <nav aria-label={label} className={`language-switcher language-switcher--${variant}`}>
      {variant === "drawer" ? <span className="language-switcher__label">{label}</span> : null}
      <div className="language-switcher__options">
        {localeSpecs.map((spec) => {
          if (!available && spec.code !== "en") {
            return <span aria-disabled="true" className="language-switcher__unavailable" dir={spec.direction} key={spec.code}>{spec.nativeLabel}</span>;
          }
          const href = available ? localePath(spec.code, basePath) : pathname ?? "/";
          // Locale changes need a fresh root document (lang, dir, and translated
          // chrome). A native link also avoids reusing a prefetched home redirect
          // that was resolved before the click updated the locale cookie.
          return (
            <a
              aria-current={spec.code === locale ? "page" : undefined}
              dir={spec.direction}
              href={href}
              hrefLang={spec.code}
              key={spec.code}
              lang={spec.code}
              onClick={() => {
                saveLocalePreference(spec.code);
                onNavigate?.();
              }}
              tabIndex={tabIndex}
            >
              {spec.nativeLabel}
            </a>
          );
        })}
      </div>
    </nav>
  );
}
